import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import './ConfirmChangesModal.scss';

type Changes = {
  machineName?: string;
  machineUpperName?: string;
  location?: string;
  releaseVersion?: string;
  addedEnvVars?: string[];
  modifiedEnvVars?: string[];
  deletedEnvVars?: string[];
};

type ConfirmChangesModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  deviceName?: string;
  storeName?: string;
  isBulk?: boolean;
  deviceCount?: number;
  changes: Changes;
  isLoading?: boolean;
};

export const ConfirmChangesModal: React.FC<ConfirmChangesModalProps> = ({
  open,
  onClose,
  onConfirm,
  deviceName,
  storeName,
  isBulk = false,
  deviceCount,
  changes,
  isLoading = false,
}) => {
  const subtitleText = useMemo(() => {
    if (isBulk && deviceCount) {
      return `The following changes will be applied to ${deviceCount} ${
        deviceCount === 1 ? 'device' : 'devices'
      }`;
    }

    if (storeName) return `The following changes will be applied to ${storeName} devices`;
    if (deviceName) return `The following changes will be applied to ${deviceName} device`;
    return 'The following changes will be applied';
  }, [isBulk, deviceCount, storeName, deviceName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: 'confirm-changes-modal',
      }}
    >
      <DialogTitle className="modal-title">Confirm Changes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" align="center" className="modal-subtitle">
          {subtitleText}
        </Typography>
        <div className="changes-list">
          {changes.machineName && (
            <div className="change-item">
              <div className="change-label">Machine Name</div>
              <div className="change-value">{changes.machineName}</div>
            </div>
          )}

          {changes.machineUpperName && (
            <div className="change-item">
              <div className="change-label">Machine Upper Name</div>
              <div className="change-value">{changes.machineUpperName}</div>
            </div>
          )}

          {changes.location && (
            <div className="change-item">
              <div className="change-label">Location</div>
              <div className="change-value">{changes.location}</div>
            </div>
          )}

          {changes.releaseVersion && (
            <div className="change-item">
              <div className="change-label">Release Version</div>
              <div className="change-value">{changes.releaseVersion}</div>
            </div>
          )}

          {changes.addedEnvVars && changes.addedEnvVars.length > 0 && (
            <div className="change-item">
              <div className="change-label">Add Environment Variables</div>
              <ul className="change-value-list">
                {changes.addedEnvVars.map((envVar, idx) => (
                  <li key={idx}>{envVar}</li>
                ))}
              </ul>
            </div>
          )}

          {changes.modifiedEnvVars && changes.modifiedEnvVars.length > 0 && (
            <div className="change-item">
              <div className="change-label">Modify Environment Variables</div>
              <ul className="change-value-list">
                {changes.modifiedEnvVars.map((envVar, idx) => (
                  <li key={idx}>{envVar}</li>
                ))}
              </ul>
            </div>
          )}

          {changes.deletedEnvVars && changes.deletedEnvVars.length > 0 && (
            <div className="change-item">
              <div className="change-label">Delete Environment Variables</div>
              <ul className="change-value-list">
                {changes.deletedEnvVars.map((envVar, idx) => (
                  <li key={idx}>{envVar}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className="modal-actions">
        <Button variant="outlined" color="primary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" disabled={isLoading}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
